import React, {useEffect, useRef, useState} from 'react';
import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Alert, AlertIcon,
  Badge, Box, Button, Code, Flex, FormControl, FormHelperText, FormLabel, Heading, Input,
  Menu, MenuButton, MenuItem, MenuList, Portal, SimpleGrid, Stack, Switch, Text, Textarea, useToast,
} from '@chakra-ui/react';
import {ChevronDownIcon} from '@chakra-ui/icons';
import {apiPost} from '../../api';
import {useVuplex} from '../../hooks/useVuplex';
import {usePermissions, devSettings} from '../../hooks/usePermissions';
import {sendPerformanceRequest, sendPerformanceProps, sendPerformanceAction} from '../../vuplex';

const requestId = () => `perf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const errorMessage = error => error?.response?.data?.message || error?.message || String(error);

// Input drafts never replace the authoritative values shown below them.
export const PerformanceNumber = ({value, control, onCommit, index}) => {
  const factor = control.name === 'maximumCachedBytes' ? 1048576 : 1;
  const [draft, setDraft] = useState(String(value / factor));
  const [editing, setEditing] = useState(false);
  const dirty = useRef(false);
  useEffect(() => {
    if (!editing) setDraft(String(value / factor));
  }, [value, factor, editing]);
  const valid = draft.trim() !== '' && Number.isFinite(Number(draft)) &&
    (!control.integer || factor !== 1 || Number.isInteger(Number(draft))) &&
    (control.min == null || Number(draft) >= control.min / factor) &&
    (control.max == null || Number(draft) <= control.max / factor);
  const commit = () => {
    if (dirty.current && valid) {
      dirty.current = false;
      onCommit(factor === 1 ? Number(draft) : Math.round(Number(draft) * factor));
    }
    if (valid) setEditing(false);
  };
  return <Box>
    <Input
      aria-label={`${control.label}${index == null ? '' : ` ${index}`}`}
      type="number" value={draft} isInvalid={!valid} isDisabled={!control.available}
      min={control.min == null ? undefined : control.min / factor}
      max={control.max == null ? undefined : control.max / factor}
      step={factor === 1 ? control.step : 1}
      onFocus={() => setEditing(true)}
      onChange={event => { setDraft(event.target.value); dirty.current = true; }}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key === 'Enter') { event.preventDefault(); commit(); }
        if (event.key === 'Escape') { dirty.current = false; setDraft(String(value / factor)); setEditing(false); event.currentTarget.blur(); }
      }}
    />
    {editing && <Text fontSize="xs">Current: {value / factor}{control.unit ? ` ${control.unit}` : ''}</Text>}
    {!valid && <Text color="red.300" fontSize="xs">Enter a valid {control.integer && factor === 1 ? 'whole ' : ''}number in range.</Text>}
  </Box>;
};

// Use the same DOM-rendered menus as DevConsole's Realtime picker: native select
// popups are not reliably visible in Vuplex's texture-rendered webview.
const PerformanceChoice = ({label, value, options, isDisabled, onChange}) => (
  <Menu matchWidth>
    <MenuButton as={Button} aria-label={label} isDisabled={isDisabled} height="auto"
      overflow="hidden" paddingY={2} rightIcon={<ChevronDownIcon/>} textAlign="left" width="full">
      <Text noOfLines={1}>{options.find(option => option.value === value)?.label ?? String(value)}</Text>
    </MenuButton>
    <Portal>
      <MenuList maxHeight="20rem" overflowY="auto">
        {options.map(option => <MenuItem key={option.value} overflow="hidden"
          fontWeight={option.value === value ? 'bold' : 'normal'}
          onClick={() => { if (option.value !== value) onChange(option.value); }}>
          <Text noOfLines={2}>{option.label}</Text>
        </MenuItem>)}
      </MenuList>
    </Portal>
  </Menu>
);

const candidateLabel = candidate => [candidate.name,
  candidate.createdOn && new Date(candidate.createdOn).toLocaleString(),
  candidate.snapshot?.context?.device].filter(Boolean).join(' · ');

const PerformanceField = ({control, value, automaticOn, onChange}) => {
  let input;
  if (!control.available || value == null) input = <Text color="gray.500">Unavailable in this session</Text>;
  else if (control.kind === 'boolean') input = <Switch aria-label={control.label} isChecked={value} onChange={event => onChange(event.target.checked)}/>;
  else if (control.options?.length) input = <PerformanceChoice label={control.label} value={value} onChange={onChange}
    options={control.options.some(option => option.value === value) ? control.options :
      [{value, label: `${value} (current)`}, ...control.options]}/>;
  else if (control.kind === 'array') input = <SimpleGrid columns={2} spacing={2}>
    {value.map((number, index) => <Box key={index}>
      <Text fontSize="xs">{control.name.startsWith('navigation') ? index === 0 ? 'SSE delta' : 'Load delta' : `Scale ${index}`}</Text>
      <PerformanceNumber value={number} control={control} index={index} onCommit={next => onChange(value.map((current, i) => i === index ? next : current))}/>
    </Box>)}
  </SimpleGrid>;
  else input = <PerformanceNumber value={value} control={control} onCommit={onChange}/>;
  return <FormControl borderWidth="1px" borderRadius="lg" p={3}>
    <FormLabel>{control.label}{control.unit && <Text as="span" fontSize="xs"> ({control.unit})</Text>}</FormLabel>
    {input}
    {control.help && <FormHelperText>{control.help}</FormHelperText>}
    {control.automatic && <FormHelperText color={automaticOn ? 'orange.300' : undefined}>
      Governed by {control.automatic}. {automaticOn ? 'Automatic quality is on and may replace your edit.' : 'Automatic quality is off.'}
    </FormHelperText>}
    {control.recreatesTileset && <Badge mt={2} colorScheme="orange">Reloads tiles</Badge>}
  </FormControl>;
};

const Performance = ({active}) => {
  const canTune = usePermissions(devSettings);
  const toast = useToast();
  const [snapshot, setSnapshot] = useState(null);
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const pending = useRef(new Map());
  const mounted = useRef(true);
  const lastMessage = useRef(0);
  const subscriptionActive = useRef(false);
  const candidateSelection = useRef(null);

  useVuplex({performanceHeartbeat: () => { lastMessage.current = Date.now(); setConnected(true); },
    performanceProps: ({snapshot: next, requestId: id, errors = []}) => {
    if (next) { setSnapshot(next); lastMessage.current = Date.now(); setConnected(true); }
    const waiter = pending.current.get(id);
    if (waiter) {
      clearTimeout(waiter.timer);
      pending.current.delete(id);
      // Unavailable fields are reported by applyQuality but do not invalidate other applied values.
      if (errors?.some(error => !error.startsWith('Unavailable:'))) waiter.reject(new Error(errors.join('; ')));
      else waiter.resolve({snapshot: next, errors});
    } else if (errors?.length) toast({status: 'error', title: 'Performance update', description: errors.join('; ')});
  }});

  const send = (sender, payload) => new Promise((resolve, reject) => {
    const id = requestId();
    const timer = setTimeout(() => {
      pending.current.delete(id);
      reject(new Error('Unity did not respond. Open the DevConsole in the app and retry.'));
    }, 5000);
    pending.current.set(id, {resolve, reject, timer});
    sender({...payload, RequestId: id});
  });
  const notifyError = error => { if (mounted.current) toast({status: 'error', title: 'Performance', description: errorMessage(error)}); };
  const refresh = () => send(sendPerformanceRequest, {Subscribe: true}).catch(notifyError);
  const change = (field, value) => send(sendPerformanceProps, {Props: {[field]: value}}).catch(notifyError);
  const action = actionName => send(sendPerformanceAction, {Action: actionName}).catch(notifyError);

  useEffect(() => {
    mounted.current = true;
    const requests = pending.current;
    return () => {
      mounted.current = false;
      for (const waiter of requests.values()) { clearTimeout(waiter.timer); waiter.reject(new Error('Performance tab closed')); }
      requests.clear();
    };
  }, []);
  useEffect(() => {
    if (!active || !canTune) return;
    const start = () => {
      if (document.hidden) return;
      subscriptionActive.current = true;
      refresh();
    };
    const stop = () => {
      subscriptionActive.current = false;
      sendPerformanceRequest({Subscribe: false});
      for (const waiter of pending.current.values()) { clearTimeout(waiter.timer); waiter.reject(new Error('Performance tab closed')); }
      pending.current.clear();
    };
    const visibility = () => document.hidden ? stop() : start();
    const interval = setInterval(() => {
      if (!document.hidden && subscriptionActive.current) {
        sendPerformanceRequest({Subscribe: true, Heartbeat: true});
        setConnected(Date.now() - lastMessage.current < 10000);
      }
    }, 2000);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', stop);
    start();
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', stop);
      stop();
    };
    // These functions access pending requests through stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canTune]);

  const listCandidates = async (before = null) => {
    const {data} = await apiPost('/dev/performanceCandidates/list', before ? {before} : {});
    if (!mounted.current) return;
    setCandidates(current => before ? [...current, ...data.candidates] : data.candidates);
    setNextCursor(data.nextCursor);
  };
  const save = async () => {
    setBusy(true);
    try {
      const {snapshot: fresh} = await send(sendPerformanceRequest, {Subscribe: true});
      const {data} = await apiPost('/dev/performanceCandidates/create', {name, notes, snapshot: fresh});
      if (!mounted.current) return;
      await listCandidates();
      candidateSelection.current = data._id;
      setSelectedId(data._id);
      setSelectedCandidate(data);
      toast({status: 'success', title: 'Candidate saved', description: 'A new candidate was created. Production presets were not changed.'});
    } catch (error) { notifyError(error); }
    finally { if (mounted.current) setBusy(false); }
  };
  const inspect = async id => {
    candidateSelection.current = id;
    setSelectedId(id);
    setSelectedCandidate(null);
    if (!id) return;
    try {
      const {data} = await apiPost('/dev/performanceCandidates/get', {id});
      if (mounted.current && candidateSelection.current === id) setSelectedCandidate(data);
    } catch (error) { notifyError(error); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const {errors} = await send(sendPerformanceAction, {Action: 'applyQuality', QualityValues: selectedCandidate.qualityPreset.performance});
      if (mounted.current) toast({status: errors?.length ? 'warning' : 'success', title: 'Candidate applied', description: errors?.length ? errors.join('; ') : 'Quality values applied; automatic-quality mode and experiments retained.'});
    } catch (error) { notifyError(error); }
    finally { if (mounted.current) setBusy(false); }
  };

  if (!canTune) return <Text>Dev settings permission is required.</Text>;
  const {values = {}, controls = [], context = {}} = snapshot || {};
  const master = controls.find(control => control.name === 'automaticQualityEnabled');
  const navigation = controls.find(control => control.name === 'navigationAutoQuality');
  const filter = search.trim().toLowerCase();
  return <Stack spacing={4}>
    <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
      {master && <PerformanceField control={master} value={values.automaticQualityEnabled} automaticOn={false} onChange={value => change(master.name, value)}/>}
      {navigation && <PerformanceField control={navigation} value={values.navigationAutoQuality} automaticOn={false} onChange={value => change(navigation.name, value)}/>}
    </SimpleGrid>
    <Flex gap={2} wrap="wrap">
      <Button onClick={refresh}>Refresh</Button>
      <Input placeholder="Search performance settings" aria-label="Search performance settings" value={search} onChange={event => setSearch(event.target.value)} maxW="24em"/>
      <Button onClick={() => action('logCurrent')} isDisabled={!snapshot}>Log runtime settings</Button>
      <Button onClick={() => action('logPreset')} isDisabled={!snapshot}>Log selected preset</Button>
      <Button onClick={() => action('recreateTileset')} isDisabled={!values.maximumScreenSpaceError}>Recreate tileset</Button>
    </Flex>
    {(!snapshot || !connected) && <Alert status="info"><AlertIcon/>{snapshot ? 'Waiting for Unity. Displayed values may be stale.' : 'Waiting for runtime performance settings from Unity…'}</Alert>}
    {snapshot && <>
      <Flex wrap="wrap" gap={3} fontSize="sm">
        <Text>{context.device} · {context.gpu} · {context.graphicsApi}</Text>
        <Text>Preset: {context.selectedPreset || 'Unavailable'}</Text>
        <Text>AutoMapDetail: {String(context.autoMapDetail ?? 'Unavailable')}</Text>
        <Text>Map: {context.mapType}</Text>
        {context.scaleIndex != null && <Text>Scale index: {context.scaleIndex}</Text>}
        {context.effectiveNearClip != null && <Text>Camera clipping: {context.effectiveNearClip}–{context.effectiveFarClip} m</Text>}
        {context.loadingProgress != null && <Text>Tiles loaded: {Number(context.loadingProgress).toFixed(1)}%</Text>}
        <Text>Effective fog: {context.effectiveFogDensity}</Text>
        {context.exclusionEnabled != null && <Text>Exclusion: {context.exclusionEnabled ? 'on' : 'off'} · diameter {context.exclusionDiameter}</Text>}
      </Flex>
      <Accordion allowMultiple defaultIndex={[0, 1]}>
        {['General', 'Cesium'].map(section => <AccordionItem key={section}>
          <AccordionButton><Heading flex={1} textAlign="left" size="md">{section}</Heading><AccordionIcon/></AccordionButton>
          <AccordionPanel>
            <SimpleGrid columns={{base: 1, md: 2, xl: 3}} spacing={3}>
              {controls.filter(control => control.section === section && control !== master && control !== navigation && `${control.label} ${control.name}`.toLowerCase().includes(filter))
                .map(control => <PerformanceField key={control.name} control={control} value={values[control.name]}
                  automaticOn={!!values.automaticQualityEnabled} onChange={value => change(control.name, value)}/>)}
            </SimpleGrid>
          </AccordionPanel>
        </AccordionItem>)}
      </Accordion>
    </>}
    <Box borderWidth="1px" borderRadius="lg" p={3}>
      <Heading size="sm" mb={2}>Quality-preset candidates</Heading>
      <Text fontSize="sm" mb={2}>Save a fresh runtime snapshot for later comparison. Applying restores quality values and keeps the current automatic mode and experiments.</Text>
      {values.automaticQualityEnabled && <Text color="orange.300" fontSize="sm">Automatic quality is on and may overwrite applied values.</Text>}
      <Stack spacing={2}>
        <Input aria-label="Candidate name" placeholder="Candidate name" maxLength={120} value={name} onChange={event => setName(event.target.value)}/>
        <Textarea aria-label="Candidate notes" placeholder="Notes (optional)" maxLength={2000} value={notes} onChange={event => setNotes(event.target.value)}/>
        <Flex gap={2} wrap="wrap">
          <Button onClick={save} isLoading={busy} isDisabled={!snapshot || !connected || !name.trim()}>Save new candidate</Button>
          <Button onClick={() => listCandidates().catch(notifyError)} isDisabled={busy}>List candidates</Button>
          {nextCursor && <Button onClick={() => listCandidates(nextCursor).catch(notifyError)}>Load more</Button>}
        </Flex>
        <PerformanceChoice label="Saved candidate" value={selectedId} isDisabled={busy} onChange={inspect}
          options={[
            {value: '', label: 'Select a saved candidate'},
            ...(selectedCandidate && !candidates.some(candidate => candidate._id === selectedId)
              ? [{value: selectedId, label: candidateLabel(selectedCandidate)}] : []),
            ...candidates.map(candidate => ({value: candidate._id, label: candidateLabel(candidate)})),
          ]}/>
        {selectedCandidate && <>
          <Text fontSize="sm">{selectedCandidate.notes}</Text>
          <Button onClick={apply} isLoading={busy} isDisabled={!snapshot || !connected}>Apply quality values</Button>
          <Box as="details"><Box as="summary" cursor="pointer">Inspect saved snapshot</Box>
            <Code as="pre" whiteSpace="pre-wrap" overflowWrap="anywhere" fontSize="xs" w="full">{JSON.stringify(selectedCandidate, null, 2)}</Code>
          </Box>
        </>}
      </Stack>
    </Box>
  </Stack>;
};

export default Performance;
